import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { AuthProvider } from './contexts/AuthContext';
import { FranchiseSelectorProvider } from './contexts/FranchiseSelectorContext';
import { ProtectedRoute, AdminRoute, FranchiseeRoute, PublicRoute } from './components/auth/ProtectedRoute';
import ScrollToTop from './components/layout/ScrollToTop';
import LoadingSpinner from './components/common/LoadingSpinner';
import ErrorBoundary from './components/common/ErrorBoundary';

// Public pages
import Home from './views/public/home';
import Contact from './views/public/contact';
import Partners from './views/public/Partners';
import ProductPage from './views/public/ProductPage';
import SunscreenFacts from './views/public/SunscreenFacts';
import HowToUse from './views/public/HowToUse';
import BlockMap from './views/dashboard/BlockMap';
import AboutUs from './views/public/AboutUs';
import Careers from './views/public/Careers';
import Checkout from './views/public/Checkout';
import Success from './views/public/Success';
import NotFound from './views/public/not-found';

// Auth pages
import Login from './views/management/auth/Login';
import ForgotPassword from './views/management/auth/ForgotPassword';
import InviteAcceptance from './views/auth/InviteAcceptance';

// Protected pages
import DashboardLayout from './components/layout/DashboardLayout';
import BlockManagement from './components/dashboard/BlockManagement';
import BlockSetup from './views/dashboard/BlockSetup';
import Settings from './views/dashboard/settings';
import SunscreenManagement from './views/dashboard/SunscreenManagement';
import UserManagement from './views/management/admin/UserManagement';
import AdminSetup from './views/management/admin/AdminSetup';
import FranchiseManagement from './views/management/franchise/FranchiseManagement';
import FranchiseeManagement from './views/management/franchise/FranchiseeManagement';

// Components
import './App.css';

// Global SEO component
const GlobalSEO = () => (
  <Helmet>
    <title>Block Automation - Smart Sunscreen Dispensers</title>
    <meta name="description" content="Manage your Block smart sunscreen dispensers with our comprehensive dashboard. Track usage, monitor devices, and ensure optimal sun protection." />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <meta name="robots" content="index, follow" />
    <meta property="og:type" content="website" />
    <meta property="og:title" content="Block Automation - Smart Sunscreen Dispensers" />
    <meta property="og:description" content="Manage your Block smart sunscreen dispensers with our comprehensive dashboard." />
    <meta property="og:image" content="/block_feature.png" />
    <meta name="twitter:card" content="summary_large_image" />
    <link rel="canonical" href="https://blockautomation.ai" />
  </Helmet>
);



function App() {
  return (
    <ErrorBoundary>
      <GlobalSEO />
    <AuthProvider>
      <FranchiseSelectorProvider>
        <Router>
          <ScrollToTop />
            <Suspense fallback={<LoadingSpinner size="large" message="Loading application..." />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/success" element={<Success />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/product" element={<ProductPage />} />
          <Route path="/sunscreen-facts" element={<SunscreenFacts />} />
          <Route path="/how-to-use" element={<HowToUse />} />
          <Route path="/block-map" element={<BlockMap />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/careers" element={<Careers />} />

          {/* Auth Routes - Redirect authenticated users */}
          <Route path="/login" element={
            <PublicRoute redirectTo="/dashboard">
              <Login />
            </PublicRoute>
          } />
          
          <Route path="/auth/forgot-password" element={
            <PublicRoute redirectTo="/dashboard">
              <ForgotPassword />
            </PublicRoute>
          } />
              
              {/* Invitation Acceptance - Available to everyone */}
              <Route path="/invite/:token" element={<InviteAcceptance />} />

          {/* Protected Dashboard Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            {/* Default dashboard route */}
            <Route index element={<Navigate to="block-management" replace />} />
            
            {/* Block Management - Available to all authenticated users */}
            <Route path="block-management" element={<BlockManagement />} />
            
            {/* Block Setup - Admin only */}
            <Route path="block-setup" element={
              <AdminRoute>
                <BlockSetup />
              </AdminRoute>
            } />
            
            {/* Settings - Available to all authenticated users */}
            <Route path="settings" element={<Settings />} />
            
            {/* Franchise Management - Franchisees and Admins only */}
            <Route path="franchise-management" element={
              <FranchiseeRoute>
                <FranchiseManagement />
              </FranchiseeRoute>
            } />

                {/* Franchisee Management - Admin only */}
                <Route path="franchisee-management" element={
                  <AdminRoute>
                    <FranchiseeManagement />
                  </AdminRoute>
                } />
            
            {/* Admin-only routes */}
            <Route path="admin-setup" element={
              <AdminRoute>
                <AdminSetup />
              </AdminRoute>
            } />
            
            <Route path="user-management" element={
              <AdminRoute>
                <UserManagement />
              </AdminRoute>
            } />
            
            <Route path="sunscreen-management" element={
              <AdminRoute>
                <SunscreenManagement />
              </AdminRoute>
            } />
          </Route>

          {/* Auth status pages */}
          <Route path="/auth/setup" element={
            <div className="auth-status-page">
              <h1>Account Setup Required</h1>
              <p>Your account is being set up. Please contact your administrator.</p>
            </div>
          } />
          
          <Route path="/auth/inactive" element={
            <div className="auth-status-page">
              <h1>Account Inactive</h1>
              <p>Your account has been deactivated. Please contact your administrator.</p>
              <p>If you believe this is an error, try <a href="/dashboard" style={{color: '#007bff', textDecoration: 'underline'}}>refreshing to the dashboard</a>.</p>
            </div>
          } />
          
          <Route path="/auth/unauthorized" element={
            <div className="auth-status-page">
              <h1>Access Denied</h1>
              <p>You don't have permission to access this page.</p>
            </div>
          } />

          {/* Catch all route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
          </Suspense>
        </Router>
      </FranchiseSelectorProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;